import { FilterSidebarToggleForm } from "./filter-sidebar-toggle-form";
import { FilterSidebarToggleHandler } from "./filter-sidebar-toggle-handler";
import { FilterSidebarToggleListener } from "./filter-sidebar-toggle-listener";

/**
 * The navbar-top mobile search/filter button is rendered on every page (it
 * lives in the shared navbar partial), but only does something on pages that
 * actually have a filter sidebar (products/posts/brands). Elsewhere it's an
 * inert button that looks tappable but does nothing — hide it there instead.
 *
 * admin/editor used to get a second, admin-search-trigger element swapped
 * into the same header slot on pages where this button is inert, so there
 * was always a working search entry point in the mobile header. That element
 * no longer exists (navbar.blade.php) — mobile admin search is hidden
 * unconditionally now (the already-tight header had no room to spare, and
 * the drawer stays the mobile fallback); admin/editor's labeled "admin"
 * search lives only in the desktop nav bar. getAdminSearchTrigger() is kept
 * as a no-op-safe lookup (returns null, falls through below) rather than
 * removed, in case that element comes back.
 *
 * Registered separately from initFilterSidebarToggle() (different selector:
 * the mobile button, not the desktop one) so it still runs on pages that
 * have no desktop toggle at all, and so it can't double up the toggle/drag
 * listeners that function wires (those use plain addEventListener, not an
 * idempotent EventScope binding).
 */
export function hideMobileFilterToggleIfInert(root: Element = document.documentElement): void {
    const form = new FilterSidebarToggleForm(root);
    const mobileBtn = form.getMobileToggleButton();
    if (!mobileBtn) return;

    const isInert = !form.getWrapper();
    mobileBtn.hidden = isInert;

    // hidden on the button alone still leaves its fixed-width .navbar-search
    // wrapper div in the flex layout as an empty 40px box — hide that too.
    const mobileBtnWrapper = form.getMobileToggleButtonWrapper();
    if (mobileBtnWrapper instanceof HTMLElement) {
        mobileBtnWrapper.hidden = isInert;
    }

    const adminSearchTrigger = form.getAdminSearchTrigger();
    if (adminSearchTrigger instanceof HTMLElement) {
        adminSearchTrigger.hidden = !isInert;
    }
}

export function initFilterSidebarToggle(root: Element = document.documentElement): void {
    const form = new FilterSidebarToggleForm(root);
    const btn = form.getToggleButton();
    const mobileBtn = form.getMobileToggleButton();
    const wrapper = form.getWrapper();
    const sidebar = form.getSidebar();
    const dragHandle = form.getDragHandle();
    if ((!btn && !mobileBtn) || !wrapper) return;

    const handler = new FilterSidebarToggleHandler();
    const listener = new FilterSidebarToggleListener(handler);

    // On mobile always start closed; on desktop restore from localStorage
    handler.applyState(wrapper, btn, mobileBtn, handler.isMobile() ? false : handler.readStoredState());

    if (btn) listener.registerToggle(btn, wrapper, btn, mobileBtn);
    if (mobileBtn) listener.registerToggle(mobileBtn, wrapper, btn, mobileBtn);
    listener.registerBackdropClose(wrapper, btn, mobileBtn);

    if (sidebar && dragHandle) {
        listener.registerDrag(dragHandle, sidebar, wrapper, btn, mobileBtn);
    }
}
